import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { GetCounterpartiesResponseDto } from "@common/dto/counterparty/get-counterparties-response.dto";
import { ViewOptions } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useCounterpartyRowHighlight } from "@components/tasks/useCounterpartyRowHighlight";
import { useLoadedRowIdResolver } from "@components/tasks/useRefRowHighlight";
import { DirectoryContext } from "./Directory.context";
import { buildCounterpartyDirectoryRows } from "./counterpartyDirectoryViewBuilder";
import { useCounterpartyDirectoryViewConfig } from "./useCounterpartyDirectoryViewConfig";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import { DirectoryCommandBar } from "./DirectoryCommandBar";
import { useCounterpartyUrlSelection } from "./useCounterpartyUrlSelection";
import { useDirectoryRowChatDrag } from "./useDirectoryRowChatDrag";
import styles from "./DirectoryTable.module.scss";

interface Props {
    counterpartiesData?: GetCounterpartiesResponseDto;
    isLoading: boolean;
    totalCounterparties: number;
    tableRef?: React.RefObject<ViewTableHandle | null>;
    expanded?: ExpandedState;
    onExpandedChange?: OnChangeFn<ExpandedState>;
    shouldExcludeNoCounterpartyRow?: boolean;
    emptyView?: () => React.ReactNode;
    reviewMode?: "review" | "unreview";
}

const defaultEmptyView = () => <EmptyState header="No counterparties found" />;

interface CounterpartyUrlSelectionProps {
    rows: CounterpartyDirectoryViewRow[];
    setSelectedRow: (row: CounterpartyDirectoryViewRow | null) => void;
    isLoading: boolean;
}

const CounterpartyUrlSelection: React.FC<CounterpartyUrlSelectionProps> = (
    props,
) => {
    useCounterpartyUrlSelection(props);
    return null;
};

export const DirectoryTable: React.FC<Props> = ({
    counterpartiesData,
    isLoading,
    totalCounterparties,
    tableRef,
    expanded,
    onExpandedChange,
    shouldExcludeNoCounterpartyRow = false,
    emptyView = defaultEmptyView,
    reviewMode,
}) => {
    const {
        filters,
        counterpartySort,
        setCounterpartySort,
        view,
        onViewChange,
        selectedRow,
        setSelectedRow,
        rowSelection,
        setRowSelection,
    } = useContext(DirectoryContext);

    const showAccountNumbers = useShowAccountNumbers();
    const { viewConfig } = useCounterpartyDirectoryViewConfig();

    const rows = useMemo(
        () =>
            buildCounterpartyDirectoryRows({
                counterparties: counterpartiesData?.data ?? [],
                noCounterpartyTotals:
                    shouldExcludeNoCounterpartyRow || filters.search
                        ? null
                        : counterpartiesData?.noCounterpartyTotals,
                showAccountNumbers,
            }),
        [
            counterpartiesData,
            filters.search,
            shouldExcludeNoCounterpartyRow,
            showAccountNumbers,
        ],
    );

    // The details panel reads the `selectedRow` snapshot from context, but
    // mutations (e.g. field reviews) patch the React Query list cache in place.
    // Re-sync the snapshot from the freshly built rows so the panel reflects the
    // latest data instead of the state captured when the row was selected.
    useEffect(() => {
        if (!selectedRow) {
            return;
        }
        const freshRow = rows.find((row) => row.id === selectedRow.id);
        if (
            freshRow &&
            (freshRow.fieldReviews !== selectedRow.fieldReviews ||
                freshRow.counterparty !== selectedRow.counterparty)
        ) {
            setSelectedRow(freshRow);
        }
    }, [rows, selectedRow, setSelectedRow]);

    const isReadonly = view.options?.readonly ?? false;
    const rowDragOut = useDirectoryRowChatDrag(rows);

    const handleRowClick = useCallback(
        (row?: CounterpartyDirectoryViewRow) => {
            if (row?.isAccountRow) {
                return;
            }
            if (!row || row.isNoCounterparty) {
                setSelectedRow(null);
                return;
            }
            setSelectedRow(selectedRow?.id === row.id ? null : row);
        },
        [selectedRow?.id, setSelectedRow],
    );

    const canSelectRow = useCallback(
        (row: CounterpartyDirectoryViewRow) =>
            !row.isAccountRow && !row.isNoCounterparty && !!row.counterparty,
        [],
    );

    const tableMeta = useMemo(
        (): ViewTableMeta<unknown, ViewOptions> => ({
            getRowSelectionHidden: (row: unknown) =>
                (row as CounterpartyDirectoryViewRow).isAccountRow,
            options: { readonly: isReadonly },
        }),
        [isReadonly],
    );

    const handleFocusedRowChange = useCallback(
        (
            _index: number | null,
            row: CounterpartyDirectoryViewRow | null,
            source: "keyboard" | "click",
        ) => {
            if (source !== "keyboard" || !selectedRow) {
                return;
            }
            if (row?.isAccountRow) {
                return;
            }
            setSelectedRow(row && !row.isNoCounterparty ? row : null);
        },
        [selectedRow, setSelectedRow],
    );

    const resolveTableRowId = useLoadedRowIdResolver(rows);
    const { viewTableRef, selectedRowId: focusedRefRowId } =
        useCounterpartyRowHighlight({ resolveTableRowId, tableRef });

    return (
        <>
            {!isReadonly && (
                <CounterpartyUrlSelection
                    rows={rows}
                    setSelectedRow={setSelectedRow}
                    isLoading={isLoading}
                />
            )}
            <ViewTable
                ref={viewTableRef}
                data={isLoading ? null : rows}
                view={view}
                onViewChange={onViewChange}
                config={viewConfig}
                sort={counterpartySort}
                onSortChange={setCounterpartySort}
                emptyView={emptyView}
                className={styles.directoryTable}
                enableColumnResizing
                rowBorders
                enableKeyboardShortcuts
                fixedTopOffsetPx
                selectedRowId={focusedRefRowId ?? selectedRow?.id ?? undefined}
                onRowClick={isReadonly ? undefined : handleRowClick}
                onFocusedRowChange={handleFocusedRowChange}
                isParentSelectable
                enableRowSelection={isReadonly ? false : canSelectRow}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                tableMeta={tableMeta}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                rowDragOut={isReadonly ? undefined : rowDragOut}
                footer={
                    isReadonly || totalCounterparties === 0 ? undefined : (
                        <DirectoryCommandBar
                            totalCounterparties={totalCounterparties}
                            rows={rows}
                            reviewMode={reviewMode}
                        />
                    )
                }
            />
        </>
    );
};
